/**
 * What a kiosk's menu pages show at one branch.
 *
 * A kiosk design is the organisation's, and branches differ in what they offer.
 * Separate designs for branches that differ is the normal answer (the Designs page
 * offers a copy when one is assigned where it does not fit); this is the safety
 * net under it, for a service retired from a branch after its design was chosen.
 *
 * The rules, all of them about not wasting a customer's touch:
 *  - a button for a service the branch does not offer is not drawn, and its space
 *    stays empty — a layout somebody drew is not rearranged;
 *  - a page with nothing on it this branch can issue is not offered: the buttons
 *    that open it are hidden too;
 *  - a grid shows only what the branch offers, and the old fallback to every
 *    service holds on the menu alone.
 *
 * Pure, because the kiosk, the editor's preview and the warnings all ask it.
 */

import { menuFor } from './kioskMenu'
import { pageIds, type LayoutDocument, type LayoutElement } from '../layout/types'

type HasId = { id: string }

/** Whether an element is drawn on a page of the choosing screen. */
function onPage(el: LayoutElement, page: string): boolean {
  if (el.screens && el.screens.length > 0 && !el.screens.includes('choosing')) return false
  return !el.pages || el.pages.length === 0 || el.pages.includes(page)
}

/**
 * The services a grid shows at this branch, in its order.
 *
 * On the menu a grid whose every chosen service is missing shows everything
 * offered, as a kiosk with one menu always did: a lobby machine with no buttons is
 * worse than one with the catalogue's order. Anywhere else that would fill a
 * "Cards" page with deposits, so an empty grid there stays empty.
 */
export function gridServices<T extends HasId>(el: LayoutElement, offered: readonly T[], isMenu: boolean): T[] {
  const chosen = el.options?.serviceIds
  if (!chosen || chosen.length === 0) return [...offered]
  if (isMenu) return menuFor(offered, chosen)
  const byId = new Map(offered.map((o) => [o.id, o]))
  return chosen.map((id) => byId.get(id)).filter((o): o is T => o !== undefined)
}

/** Whether a page has anything this branch can issue, directly or through the
 *  pages its buttons open. */
export function pageIssues(doc: LayoutDocument, page: string, offered: readonly HasId[]): boolean {
  return issuesFrom(doc, page, new Set(offered.map((s) => s.id)), offered, new Set())
}

function issuesFrom(
  doc: LayoutDocument,
  page: string,
  ids: ReadonlySet<string>,
  offered: readonly HasId[],
  seen: Set<string>,
): boolean {
  if (seen.has(page)) return false
  seen.add(page)
  const isMenu = page === pageIds(doc)[0]
  for (const el of doc.elements) {
    if (!onPage(el, page)) continue
    if (el.kind === 'services' && gridServices(el, offered, isMenu).length > 0) return true
    if (el.kind !== 'button' || !el.action) continue
    if (el.action.kind === 'service' && ids.has(el.action.serviceId)) return true
    if (el.action.kind === 'page' && issuesFrom(doc, el.action.pageId, ids, offered, seen)) return true
  }
  return false
}

/** The buttons not drawn at this branch: services it does not offer, and pages
 *  with nothing in them it can issue. */
export function hiddenOn(doc: LayoutDocument, offered: readonly HasId[]): Set<string> {
  const ids = new Set(offered.map((s) => s.id))
  const hidden = new Set<string>()
  const pageCache = new Map<string, boolean>()
  for (const el of doc.elements) {
    if (el.kind !== 'button' || !el.action) continue
    if (el.action.kind === 'service' && !ids.has(el.action.serviceId)) hidden.add(el.id)
    if (el.action.kind === 'page') {
      const target = el.action.pageId
      if (!pageCache.has(target)) pageCache.set(target, pageIssues(doc, target, offered))
      if (!pageCache.get(target)) hidden.add(el.id)
    }
  }
  return hidden
}

/**
 * The services this branch offers that no page a customer can reach will issue.
 *
 * Reachable is the menu and whatever its page buttons open, all the way down. A
 * customer cannot complain about a button they never saw, so this is said to
 * whoever looks after the machine rather than to them.
 */
export function unreachableServices(doc: LayoutDocument, offered: readonly HasId[]): string[] {
  const reached = new Set<string>()
  const seen = new Set<string>()
  const menu = pageIds(doc)[0]!
  const queue = [menu]
  while (queue.length > 0) {
    const page = queue.shift()!
    if (seen.has(page)) continue
    seen.add(page)
    for (const el of doc.elements) {
      if (el.hidden || !onPage(el, page)) continue
      if (el.kind === 'services') for (const s of gridServices(el, offered, page === menu)) reached.add(s.id)
      if (el.kind !== 'button' || !el.action) continue
      if (el.action.kind === 'service') reached.add(el.action.serviceId)
      if (el.action.kind === 'page') queue.push(el.action.pageId)
    }
  }
  return offered.map((s) => s.id).filter((id) => !reached.has(id))
}

/** The pages a customer came through to where they pressed, the menu left out:
 *  what the kiosk sends with the ticket, by id, for the per-ticket report's
 *  Section column. The server names them from the published design. */
export function menuPathOf(trail: readonly string[], page: string, menuPage: string): string[] {
  return [...trail, page].filter((p) => p !== menuPage)
}
